/**
 * Export PDF Dialog
 * Modal de exportação de PDF da ficha de imóvel — escolha de variante
 * (cliente/interna) + geração com loading + download blob.
 * Anatomia copiada de DeleteDocumentDialog (tema claro fixo, portal, z-[60],
 * NÃO fecha ao clicar fora — DS §Overlays dismissal).
 */

import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Icon } from '@/shared/icons';
import { databasesApi } from '../services/api';

export interface ExportPdfDialogProps {
  isOpen: boolean;
  onClose: () => void;
  documentId: string;
  reference?: string;
}

type Variant = 'client' | 'internal';

const VARIANT_OPTIONS: Array<{ value: Variant; title: string; description: string; testid: string }> = [
  { value: 'client', title: 'Material para cliente', description: 'Ficha comercial com fotos e dados públicos do imóvel.', testid: 'export-variant-client' },
  { value: 'internal', title: 'Ficha interna', description: 'Versão completa com descrição interna e todas as oportunidades.', testid: 'export-variant-internal' },
];

export const ExportPdfDialog: React.FC<ExportPdfDialogProps> = ({ isOpen, onClose, documentId, reference }) => {
  const [variant, setVariant] = useState<Variant>('client');
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // O componente fica montado (só escondido via `isOpen`) — reset do estado
  // interno na transição de abertura, senão banner de erro/loading de uma
  // tentativa anterior "vaza" pro próximo open. Reseta também `variant` pro
  // default ('client') a cada abertura — decisão: escolha de variante não
  // deve "grudar" de uma exportação pra outra dentro da mesma sessão do
  // modal. Hook declarado ANTES do early-return (regras de hooks).
  useEffect(() => {
    if (isOpen) {
      setVariant('client');
      setError(null);
      setIsGenerating(false);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleExport = async () => {
    try {
      setIsGenerating(true);
      setError(null);
      const blob = await databasesApi.exportPropertyPdf(documentId, variant);
      const filename = `imovel-${(reference || documentId).replace(/[^a-zA-Z0-9_-]/g, '-')}-${variant === 'client' ? 'cliente' : 'interna'}.pdf`;
      const objectUrl = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = objectUrl;
      anchor.download = filename;
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
      URL.revokeObjectURL(objectUrl);
      onClose();
    } catch (err) {
      console.error('Erro ao exportar PDF do imóvel:', err);
      setError('Não foi possível gerar o PDF. Tente novamente em instantes.');
    } finally {
      setIsGenerating(false);
    }
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center px-4"
      style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
      /* exportação: NÃO fecha ao clicar fora (DS §Overlays dismissal) */
      data-testid="property-export-pdf-modal"
    >
      <div
        className="relative rounded-lg shadow-xl max-w-md w-full"
        style={{ backgroundColor: '#ffffff' }}
        onClick={(e) => e.stopPropagation()}
        data-testid="export-pdf-dialog"
      >
        {/* Header */}
        <div
          className="flex items-center justify-between p-4 border-b"
          style={{ borderColor: '#e5e7eb' }}
        >
          <div className="flex items-center gap-3">
            <div
              className="w-10 h-10 rounded-full flex items-center justify-center"
              style={{ backgroundColor: '#F3E8FF' }}
            >
              <Icon name="download" className="w-5 h-5" style={{ color: '#7C3AED' }} />
            </div>
            <div>
              <h2 className="text-lg font-semibold" style={{ color: '#1e293b' }}>
                Exportar PDF
              </h2>
              <p className="text-sm" style={{ color: '#64748b' }}>
                Escolha a versão da ficha do imóvel
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1 rounded transition-colors"
            style={{ color: '#64748b' }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#f1f5f9';
              e.currentTarget.style.color = '#1e293b';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
              e.currentTarget.style.color = '#64748b';
            }}
            disabled={isGenerating}
            title="Fechar"
            data-testid="export-pdf-close"
          >
            <Icon name="x" className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          <div className="flex flex-col gap-2">
            {VARIANT_OPTIONS.map((option) => (
              <button
                key={option.value}
                onClick={() => setVariant(option.value)}
                disabled={isGenerating}
                className="w-full text-left rounded-lg border p-3 transition-colors"
                style={{
                  borderColor: variant === option.value ? '#7C3AED' : '#e5e7eb',
                  backgroundColor: variant === option.value ? '#FAF5FF' : '#ffffff',
                }}
                data-testid={option.testid}
              >
                <div className="text-sm font-semibold" style={{ color: '#1e293b' }}>
                  {option.title}
                </div>
                <div className="text-xs mt-0.5" style={{ color: '#64748b' }}>
                  {option.description}
                </div>
              </button>
            ))}
          </div>

          {error && (
            <div
              className="p-3 rounded-lg flex items-start gap-3 mt-3"
              style={{
                backgroundColor: '#FEE2E2',
                border: '1px solid #FECACA',
              }}
            >
              <Icon name="alert-triangle" className="w-5 h-5 flex-shrink-0" style={{ color: '#DC2626' }} />
              <p className="text-sm" style={{ color: '#991B1B' }} data-testid="export-pdf-error">
                {error}
              </p>
            </div>
          )}

          {isGenerating && (
            <p className="text-sm mt-3" style={{ color: '#64748b' }} data-testid="export-pdf-loading">
              Gerando PDF… a primeira geração pode levar alguns segundos.
            </p>
          )}
        </div>

        {/* Footer */}
        <div
          className="flex items-center justify-end gap-3 px-6 py-4 border-t"
          style={{
            backgroundColor: '#f9fafb',
            borderColor: '#e5e7eb',
          }}
        >
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium border rounded-lg transition-colors"
            style={{
              color: '#1e293b',
              backgroundColor: '#ffffff',
              borderColor: '#e5e7eb',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#f1f5f9';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = '#ffffff';
            }}
            disabled={isGenerating}
            data-testid="export-pdf-cancel"
          >
            Cancelar
          </button>
          <button
            onClick={handleExport}
            className="px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors flex items-center gap-2"
            style={{ backgroundColor: '#7C3AED', opacity: isGenerating ? 0.7 : 1 }}
            onMouseEnter={(e) => {
              if (!isGenerating) e.currentTarget.style.backgroundColor = '#6D28D9';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = '#7C3AED';
            }}
            disabled={isGenerating}
            data-testid="export-pdf-confirm"
          >
            {isGenerating ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Gerando...
              </>
            ) : (
              <>
                <Icon name="download" className="w-4 h-4" />
                Exportar
              </>
            )}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};
